/** @jsxImportSource theme-ui */
import React, { ReactNode } from 'react';

import { Typography } from 'components/_common/Typography';
import BdPrimaryLink from 'components/_controls/BdLink/BdPrimaryLink';

const MethodsLinkTooltipContent = () => {
  const mathodsLinkStyle = {
    display: 'inline-block',
    color: 'white',
    textDecoration: 'underline',
    '&:hover': {
      color: 'white',
      textDecoration: 'underline',
    },
    '&:focus': {
      color: 'white',
      textDecoration: 'underline',
      boxShadow: 'white',
      borderRadius: '2px',
    },
    '&:active': {
      color: 'white',
      textDecoration: 'underline',
    },
  };

  return (
    <Typography variant="caption" color="deepSea40" data-testid="view-methods-tooltip">
      You can read more about our methodology by visiting our{' '}
      <BdPrimaryLink href="/covid-ds/methods" variant="caption" sx={mathodsLinkStyle} inline>
        Methods
      </BdPrimaryLink>{' '}
      section.
    </Typography>
  );
};

type SimpleTooltipContentProps = { title: string; content: string };

const SimpleTooltipContent: React.FC<SimpleTooltipContentProps> = ({ title, content }) => {
  return (
    <>
      <Typography
        variant="h6"
        color="white"
        marginBottom="16px"
        sx={{ mr: '44px', whiteSpace: 'pre-line' }}
      >
        {title}
      </Typography>
      <Typography variant="caption" color="deepSea40" marginBottom="16px">
        {content}
      </Typography>
      <MethodsLinkTooltipContent />
    </>
  );
};

type MultiSectionTooltipContentProps = {
  title: string;
  superTitle?: string;
  subtitles: string[];
  contents: string[];
};

const MultiSectionTooltipContent: React.FC<MultiSectionTooltipContentProps> = ({
  title,
  superTitle,
  subtitles,
  contents,
}) => {
  return (
    <>
      <Typography variant="h6" color="white" marginBottom="16px" sx={{ mr: '44px' }}>
        {!!superTitle?.length && <Typography variant="subtitle2">{superTitle}</Typography>}
        {title}
      </Typography>
      {subtitles.map((subtitle, i) => (
        <div key={subtitle}>
          <Typography variant="subtitle2" color="white" marginBottom="4px">
            {subtitle}
          </Typography>
          {contents[i] && (
            <Typography variant="caption" color="deepSea40" marginBottom="16px">
              {contents[i]}
            </Typography>
          )}
        </div>
      ))}
      <MethodsLinkTooltipContent />
    </>
  );
};

export const PopVaccinatedHighRisk: ReactNode = (
  <SimpleTooltipContent
    title="Estimated high-risk population fully vaccinated"
    content="This is the estimated percentage of the high-risk population that has been fully vaccinated against COVID-19.​"
  />
);

export const PopVaccinatedPerDayHighRisk: ReactNode = (
  <SimpleTooltipContent
    title="Estimated high-risk population fully vaccinated / day (7-day average)"
    content="This is the estimated daily rate of the high-risk population being fully vaccinated against COVID-19. We present a 7-day rolling average of people fully vaccinated per day to account for day-by-day variations in reporting.​"
  />
);

export const MilestoneCompleteDateHighRisk: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date"
    content="This is the projected date where at least 80% of the high-risk population is estimated to have received at least one dose of a COVID-19 vaccine. High vaccination rates among high-risk groups likely correlate to reduced rates of hospitalization and deaths."
  />
);

export const ContainmentLearnMore: React.ReactElement = (
  <MultiSectionTooltipContent
    title="Containment and health index score"
    subtitles={[
      'Why does this matter?',
      'How is strictness and comprehensiveness in government policy measured?',
      'How were these variables calculated?​',
    ]}
    contents={[
      'Changes to strictness and comprehensiveness in government policy can impact daily case rates approximately 2-3 weeks later. For example, an appropriate increase in strictness in government policy may result in a decrease in daily case rates.',
      'We measure strictness and comprehensiveness in government policy related to COVID-19 using the containment and health index, from the Oxford COVID-19 Government Response Tracker (OxCGRT). Index scores are reported from 0-100, where a higher score represents policies that more strict or comprehensive.',
    ]}
  />
);

export const PopImmuneTotalTotalPopulation: ReactNode = (
  <SimpleTooltipContent
    title="Total population with a level of immunity"
    content="This is the percentage of the total population (including children) that has received at least one vaccine dose or acquired a level of natural immunity via a previous SARS-CoV-2 infection."
  />
);

export const PopImmuneInfectedTotalPopulation: ReactNode = (
  <SimpleTooltipContent
    title="Estimated total population recovered, unvaccinated"
    content="This is the estimated percentage of the total population that is unvaccinated but has acquired a level of immunity via a previous SARS-CoV-2 infection."
  />
);

export const PopFullyVaccinated: ReactNode = (
  <SimpleTooltipContent
    title="Total population fully vaccinated"
    content="This is the percentage of the total population (including children) that has been fully vaccinated against COVID-19 (have the second dose of a two-dose vaccine or one dose of a single-dose vaccine)."
  />
);

export const PopWithBooster: ReactNode = (
  <SimpleTooltipContent
    title="Total population with booster"
    content="This is the percentage of the total population (including children) who are fully vaccinated (have the second dose of a two-dose vaccine or one dose of a single-dose vaccine) and have one additional dose of a COVID-19 vaccine."
  />
);

export const PopFullyVaccinadtedDailyRate: ReactNode = (
  <SimpleTooltipContent
    title="Total population fully vaccinated / day (7-day average)"
    content="This is the daily rate of the total population being fully vaccinated against COVID-19 (excluding boosters). We present a 7-day rolling average of people fully vaccinated per day (excluding boosters) to account for day-by-day variations in reporting.​"
  />
);

export const PopImmuneVaccinatedTotalPopulation: ReactNode = (
  <SimpleTooltipContent
    title="Total population vaccinated (at least one dose)​"
    content="This is the percentage of the total population (including children) that has received at least one dose of a COVID-19 vaccine."
  />
);

export const PopVaccinatedPerDayTotalPopulation: ReactNode = (
  <SimpleTooltipContent
    title="% pop. immune per day"
    content="This is the daily rate of the total population being vaccinated. We present a 7-day rolling average of new vaccinations per day to filter out day-by-day variations in reporting."
  />
);

export const DailyCasesPer100000: ReactNode = (
  <SimpleTooltipContent
    title="Daily cases / 100,000"
    content="This is the number of daily cases per 100,000 people in a 7-day rolling average."
  />
);

export const TestPositivity: ReactNode = (
  <SimpleTooltipContent
    title="Test positivity (last 14 days)"
    content="This is the percentage of positive tests reported within the last 14 days. A test positivity rate greater than 5% suggests that an underreporting of cases is likely."
  />
);

export const MilestoneCompleteDateTotalPopulation: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date"
    content="This is the projected date where at least 65% of the total population has achieved immunity via a vaccine or prior infection. High vaccination rates and natural immunity among the total population likely correlate to the ability to reduce government restrictions without necessitating a reversal of these reopenings."
  />
);

export const HighRiskBestCaseTooltip: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date (best case)"
    content="This is the projected best-case-scenario date for when at least 80% of the high-risk population is estimated to have received at least one dose of a COVID-19 vaccine. This date assumes that a high percentage of the population is willing to be vaccinated."
  />
);

export const HighRiskModerateCaseTooltip: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date (moderate case)"
    content="This is the projected moderate-case-scenario date for when at least 80% of the high-risk population is estimated to have received at least one dose of a COVID-19 vaccine. This date assumes that a moderate percentage of the population is willing to be vaccinated."
  />
);

export const HighRiskWorstCaseTooltip: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date (worst case)"
    content="This is the projected worst-case-scenario date for when at least 80% of the high-risk population is estimated to have received at least one dose of a COVID-19 vaccine. This date assumes that a low percentage of the population is willing to be vaccinated."
  />
);

export const TotalPopBestCaseTooltip: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date (best case)"
    content="This is the projected best-case-scenario date for when at least 65% of the total population will achieve immunity via a vaccine or prior infection. This date assumes that a high percentage of the population is willing to be vaccinated."
  />
);

export const TotalPopModerateCaseTooltip: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date (moderate case)"
    content="This is the projected moderate-case-scenario date for when at least 65% of the total population will achieve immunity via a vaccine or prior infection. This date assumes that a moderate percentage of the population is willing to be vaccinated."
  />
);

export const TotalPopWorstCaseTooltip: ReactNode = (
  <SimpleTooltipContent
    title="Projected completion date (worst case)"
    content="This is the projected worst-case-scenario date for when at least 65% of the total population will achieve immunity via a vaccine or prior infection. This date assumes that a low percentage of the population is willing to be vaccinated."
  />
);

export const TotalPopulationLearnMore: React.ReactElement = (
  <MultiSectionTooltipContent
    title="Population immunity"
    subtitles={[
      'Why does this matter?',
      '​How is the population with a level of immunity measured?​',
      'How were these variables calculated?​',
    ]}
    contents={[
      'High coverage of population that is fully vaccinated offers a level of protection against COVID-19 related hospitalizations and deaths, for those vaccinated. However, high levels of transmission leading to more cases can still result in increased number of hospitalizations and deaths at the population-level.',
      'We measure multiple variables to represent the population with a level of immunity. We report the percentage of the total population (including children) that has been fully vaccinated against COVID-19; this population has the second dose of a two-dose vaccine or one dose of a single-dose vaccine. We report the percent of the total population with a booster dose; this population has been fully vaccinated as well as have had one additional dose of a COVID-19 vaccine. We report the percent of the total population (including children) that has received at least one dose of a COVID-19 vaccine or acquired a level of natural immunity via a previous SARS-CoV-2 infection; this percent of the population likely has a lower level of immunity than those fully vaccinated or with a booster dose. We also estimate the percentage of the high-risk population that has been fully vaccinated against COVID-19. The high-risk population is comprised of those over 65, those who work in high-exposure environments, or have at least one underlying condition that puts them at increased risk of severe manifestations of COVID-19.',
    ]}
  />
);

export const CaseIncidenceLearnMore: React.ReactElement = (
  <MultiSectionTooltipContent
    title="Daily case levels"
    subtitles={[
      'Why does this matter?',
      '​How is the population with a level of immunity measured​?',
      'How were these variables calculated?​',
    ]}
    contents={[
      'With anything less than herd immunity, a resurgence of cases may result in increased hospitalizations and deaths, as well as additional government restrictions being implemented. A test positivity value ≥5% suggests that underreporting of cases is likely.',
      'We describe daily case levels using the 7-day average number of reported cases per 100,000 as one of the following categories: “low” (<10), “moderate” (≥10 and <25), or “high” (≥25). We also measure the trend in daily cases over the past 14 days, as well as the test positivity over the past 14 days.',
    ]}
  />
);

export const ContainmentIndexScore: ReactNode = (
  <SimpleTooltipContent
    title="Containment and health index score"
    content="This is a score from 0-100 that represents strictness and comprehensiveness in government policies related to COVID-19. A higher score represents a response that is more strict and more comprehensive.​"
  />
);

export const ContainmentIndexScoreChange: ReactNode = (
  <SimpleTooltipContent
    title="Change over the past 30 days"
    content="This represents that change in the containment and health index score over the past 30 days.​"
  />
);
